import { Injectable } from '@angular/core';
import { StorageService } from 'common/services/core-services/storage.service';
import { BehaviorSubject } from 'rxjs';

interface CountData {
  totalCount: number;
  statusCount: number;
  currentStatus: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApplicantCountStorageService {
  private totalCountSubject = new BehaviorSubject<CountData>({
    totalCount: 0,
    statusCount: 0,
    currentStatus: ''
  });

  public totalCount$ = this.totalCountSubject.asObservable();

  constructor(private storageService: StorageService) {
    this.updateTotalCount();
  }

  private updateTotalCount(status: string = ''): void {
    const counts = this.calculateCounts(status);
    this.totalCountSubject.next(counts);
  }

  getTotalCount(): number {
    return this.calculateCounts().totalCount;
  }

  getStatusCount(status: string): number {
    const sessionData = sessionStorage.getItem('previeDetails');
    if (!sessionData) return 0;

    const parsedData = this.storageService.decrypt(sessionData); // Use StorageService
    if (!parsedData) return 0;

    const statusKey = this.getStatusKey(status);
    return parsedData[statusKey]?.length || 0;
  }

  private getStatusKey(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Applied': 'applied',
      'Due Diligence': 'duediligence',
      'Documentation': 'documentation',
      'Assessment': 'assessment',
      'Interview': 'interview',
      'Home Visit': 'homevisit',
      'Shortlisting': 'shortlisting',
      'Reject': 'reject',
    };
    return statusMap[status] || status.toLowerCase();
  }

  private calculateCounts(status: string = ''): CountData {
    const sessionData = sessionStorage.getItem('previeDetails');
    if (!sessionData) {
      return { totalCount: 0, statusCount: 0, currentStatus: status };
    }

    const parsedData = this.storageService.decrypt(sessionData); // Use StorageService
    if (!parsedData) {
      return { totalCount: 0, statusCount: 0, currentStatus: status };
    }

    // Calculate total count
    const totalCount =
      (parsedData.applied?.length || 0) +
      (parsedData.duediligence?.length || 0) +
      (parsedData.documentation?.length || 0) +
      (parsedData.assessment?.length || 0) +
      (parsedData.interview?.length || 0) +
      (parsedData.homevisit?.length || 0) +
      (parsedData.shortlisting?.length || 0) +
      (parsedData.reject?.length || 0);

    // Get status-specific count if status is provided
    const statusCount = status ? (parsedData[this.getStatusKey(status)]?.length || 0) : 0;

    return {
      totalCount,
      statusCount,
      currentStatus: status,
    };
  }

  refreshCount(status: string = ''): void {
    this.updateTotalCount(status);
  }

  getLastAddedRowStatus(): string | null {
    const sessionData = sessionStorage.getItem('previeDetails');
    if (!sessionData) return null;

    const parsedData = this.storageService.decrypt(sessionData); // Use StorageService
    if (!parsedData) return null;

    // Define status keys in the order of their occurrence
    const statusKeys = [
      'applied', 'duediligence', 'documentation', 'assessment',
      'interview', 'homevisit', 'shortlisting', 'reject',
    ];

    for (const status of statusKeys.reverse()) {
      const rows = parsedData[status];
      if (rows && rows.length > 0) {
        return this.capitalizeStatus(status); // Capitalize the status name if needed
      }
    }

    return null; // No rows found
  }

  private capitalizeStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'applied': 'Applied',
      'duediligence': 'Due Diligence',
      'documentation': 'Documentation',
      'assessment': 'Assessment',
      'interview': 'Interview',
      'homevisit': 'Home Visit',
      'shortlisting': 'Shortlisting',
      'reject': 'Reject',
    };

    return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
  }
}
